import { faEdit, faList, faProjectDiagram, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ButtonGroup } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import AppContext from '../../Authentication/AppContext'
import Authorization from '../../Authentication/Authorization'
import AuthService from '../../Authentication/AuthService'
import DialogContext from '../../Authentication/DialogContext'
import { AssignDialogProps } from '../../components/AssignDialog'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import { FlipCard } from '../../components/FlipCard'
import ProtectedButton from '../../components/ProtectedButton'
import { SelectDialogProps } from '../../components/SelectDialog'
import Loading from '../../layout/Loading'
import RoleService from '../../service/RoleService'
import GatewayType from '../GatewayType'

const Role: GatewayType = () => {
  const { t } = useTranslation()
  const [roles, setRoles] = useState([] as any[])
  const [loading, setLoading] = useState(true)
  const { userPermissions } = useContext(AppContext)
  const context = useContext(DialogContext)

  useEffect(() => {
    RoleService.getRoles(setRoles).finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    const { confirmDialogPropsSetter, editDialogPropsSetter, assignDialogPropsSetter } = context
    return [
      {
        text: t('name.text'),
        dataField: 'name',
        sort: true,
        classes: 'position-relative',
        formatter: (cellData: any, row: any) => {
          return row.editable && AuthService.hasPermission(userPermissions, Authorization.userManagement.administration.editRole) ? (
            <ProtectedButton
              variant='link'
              className='text-decoration-none pl-0 stretched-link text-left'
              tooltip={t('edit')}
              onClick={() => editRoleDialog(t, editDialogPropsSetter, setRoles, row)}>
              <FontAwesomeIcon icon={faEdit} /> {cellData}
            </ProtectedButton>
          ) : (
            cellData
          )
        },
      },
      {
        text: t('description.text'),
        dataField: 'description',
        sort: true,
      },
      {
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        headerAlign: 'left',
        sort: true,
        sortValue: (cell: any, row: any) => row.type,
        classes: 'text-center',
        formatter: (cellData: any, row: any) => {
          if (row.type === 'DEFAULT') {
            return (
              <ProtectedButton
                permissions={Authorization.userManagement.administration.viewRole}
                variant='link'
                className='text-decoration-none'
                tooltip={t('role.viewPermisions')}
                onClick={() => viewPermissionsDialog(t, confirmDialogPropsSetter, row)}>
                <FontAwesomeIcon icon={faList} />
              </ProtectedButton>
            )
          }
          return (
            <>
              <ProtectedButton
                permissions={Authorization.userManagement.administration.editRole}
                variant='link'
                className='text-decoration-none'
                tooltip={t('role.assignPermissions')}
                onClick={() => assignPermissionsDialog(t, assignDialogPropsSetter, row)}>
                <FontAwesomeIcon icon={faProjectDiagram} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.userManagement.administration.editRole}
                shouldRender={row.deletable}
                variant='link'
                className='text-danger text-link text-decoration-none pl-2'
                tooltip={t('delete')}
                onClick={() => deleteRoleDialog(t, confirmDialogPropsSetter, setRoles, row)}>
                <FontAwesomeIcon icon={faTrashAlt} />
              </ProtectedButton>
            </>
          )
        },
      },
    ]
  }, [t, userPermissions, context])

  const defaultSorted = [
    {
      dataField: 'df1',
      order: 'asc',
    },
  ]

  return (
    <Loading isLoading={loading}>
      <DataTable
        data={roles}
        columns={columns}
        caption={t('role.text_plural')}
        helpLink={t('role.helpLink')}
        captionButtons={[
          {
            onClick: () => createRoleDialog(t, context.editDialogPropsSetter, setRoles),
            text: t('new'),
            permissions: Authorization.userManagement.administration.editRole,
          },
        ]}
        defaultSorted={defaultSorted}
      />
    </Loading>
  )
}

function createRoleDialog(t: TFunction, editDialogPropsSetter: any, setRoles: React.Dispatch<any>) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('role.createHeader'),
    onSubmit: RoleService.createRole,
    onSubmitExtraArgs: [setRoles, t],
    data: [
      {
        id: 'name',
        type: 'text',
        label: t('name.text'),
        placeholder: t('name.text'),
        requiredMessage: t('name.inValidMessage'),
        required: true,
      },
      {
        id: 'description',
        type: 'text',
        label: t('description.text'),
        placeholder: t('description.text'),
        requiredMessage: t('description.inValidMessage'),
        required: true,
      },
    ],
  }

  editDialogPropsSetter(editDialogProps)
}

function editRoleDialog(t: TFunction, editDialogPropsSetter: any, setRoles: React.Dispatch<any>, row: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('role.editHeader'),
    onSubmit: RoleService.updateRole,
    onSubmitExtraArgs: [setRoles, t],
    data: [
      {
        id: 'id',
        value: row.id,
        type: 'hidden',
        render: false,
      },
      {
        id: 'name',
        value: row.name,
        type: 'text',
        label: t('name.text'),
        placeholder: t('name.text'),
        requiredMessage: t('name.inValidMessage'),
        required: true,
      },
      {
        id: 'description',
        value: row.description,
        type: 'text',
        label: t('description.text'),
        placeholder: t('description.text'),
        requiredMessage: t('description.inValidMessage'),
        required: true,
      },
    ],
  }

  editDialogPropsSetter(editDialogProps)
}

function deleteRoleDialog(t: TFunction, confirmDialogPropsSetter: any, setRoles: React.Dispatch<React.SetStateAction<any[]>>, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('role.deleteHeader'),
    body: t('role.deleteBody', { name: row.name }),
    submitText: t('yes'),
    onSubmit: RoleService.deleteRole,
    onSubmitExtraArgs: [row, setRoles, t],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

function viewPermissionsDialog(t: TFunction, confirmDialogPropsSetter: any, row: any) {
  const columns = [
    {
      text: t('name.text'),
      dataField: 'name',
      sort: true,
    },
    {
      text: t('description.text'),
      dataField: 'description',
      sort: true,
    },
  ]

  RoleService.getAssignedPermissions(row.id).then((roles) => {
    const deleteDialogProps: ConfirmDialogProps = {
      show: true,
      header: t('role.assignedPermissionsHeader'),
      body: <DataTable data={roles} columns={columns} condensed={true} />,
      submitText: t('ok'),
      showCancelButton: false,
    }
    confirmDialogPropsSetter(deleteDialogProps)
  })
}

function assignPermissionsDialog(t: TFunction, assignDialogPropsSetter: any, row: any) {
  RoleService.getPermissionsWithAssigned(row.id).then((roles) => {
    const assignDialogProps: AssignDialogProps = {
      show: true,
      header: t('role.assignHeader', { name: row.name }),
      onSubmit: RoleService.assignPermissions,
      onSubmitExtraArgs: [row.id, row.name, t],
      data: roles,
      columns: [
        {
          text: t('name.text'),
          dataField: 'name',
          sort: true,
        },
        {
          text: t('description.text'),
          dataField: 'description',
          sort: true,
        },
        {
          dataField: 'isAssigned',
          text: t('assign'),
          isAssignField: true,
        },
      ],
    }
    assignDialogPropsSetter(assignDialogProps)
  })
}

function selectRoleDialog(t: TFunction, selectDialogPropsSetter: any, roles: any[], onSubmit: any, onSubmitExtraArgs?: any[]) {
  const selectDialogProps: SelectDialogProps = {
    show: true,
    header: t('role.selectHeader'),
    data: roles,
    keyField: 'name',
    label: t('role.selectHeader'),
    submitText: t('select'),
    onSubmit,
    onSubmitExtraArgs,
    cancelText: t('cancel'),
  }
  selectDialogPropsSetter(selectDialogProps)
}

export const RoleFlipCard = () => {
  const { t } = useTranslation()
  const { userPermissions } = useContext(AppContext)
  const { selectDialogPropsSetter, editDialogPropsSetter, confirmDialogPropsSetter, assignDialogPropsSetter } = useContext(DialogContext)
  const [roles, setRoles] = useState([] as any[])

  const viewRole = Authorization.userManagement.administration.viewRole
  const editRole = Authorization.userManagement.administration.editRole

  useEffect(() => {
    if (AuthService.hasPermission(userPermissions, viewRole)) {
      RoleService.getRoles(setRoles)
    }
  }, [userPermissions, viewRole])

  return (
    <FlipCard
      permissions={viewRole}
      headerText={t('role.text')}
      headerLink='./userManagement/role'
      flipFrontContent={
        <ButtonGroup vertical>
          <ProtectedButton permissions={editRole} variant='link' className='m-1' onClick={() => createRoleDialog(t, editDialogPropsSetter, setRoles)}>
            {t('role.createHeader')}
          </ProtectedButton>
          {roles.find((role) => role.editable) && (
            <>
              <ProtectedButton
                permissions={editRole}
                variant='link'
                className='m-1'
                onClick={() =>
                  selectRoleDialog(
                    t,
                    selectDialogPropsSetter,
                    roles.filter((role) => role.editable),
                    editRoleDialog,
                    [t, editDialogPropsSetter, setRoles]
                  )
                }>
                {t('role.editHeader')}
              </ProtectedButton>
              <ProtectedButton
                permissions={editRole}
                variant='link'
                className='m-1'
                onClick={() =>
                  selectRoleDialog(
                    t,
                    selectDialogPropsSetter,
                    roles.filter((role) => role.editable),
                    assignPermissionsDialog,
                    [t, assignDialogPropsSetter]
                  )
                }>
                {t('role.assignPermissions')}
              </ProtectedButton>
            </>
          )}
          {roles.find((role) => role.deletable) && (
            <ProtectedButton
              permissions={editRole}
              variant='link'
              className='m-1'
              onClick={() =>
                selectRoleDialog(
                  t,
                  selectDialogPropsSetter,
                  roles.filter((role) => role.deletable),
                  deleteRoleDialog,
                  [t, confirmDialogPropsSetter, setRoles]
                )
              }>
              {t('role.deleteHeader')}
            </ProtectedButton>
          )}
        </ButtonGroup>
      }
    />
  )
}

export default Role
